import { Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

/** Fields that name another resource's row. */
export const LINKS: Record<string, string> = {
	competition: 'competitions',
	current_season: 'seasons',
	season: 'seasons',
	binding: 'bindings',
	run: 'runs',
	last_run: 'runs',
	last_run_id: 'runs',
	provider: 'providers',
	provider_id: 'providers',
};

const COLORS: Record<string, string> = {
	ok: 'green',
	finished: 'green',
	confirmed: 'green',
	partial: 'gold',
	warning: 'gold',
	live: 'blue',
	running: 'blue',
	scheduled: 'default',
	failed: 'red',
	error: 'red',
	critical: 'red',
	postponed: 'orange',
	cancelled: 'orange',
};

const TAGGED = new Set( [ 'outcome', 'status', 'severity', 'last_outcome', 'mode', 'kind', 'format', 'result_status', 'type' ] );
const isDateKey = ( key: string ) => /(_at|_start|_date|^date_of_birth)$/.test( key );

/** A value as a table cell or description: links, tags, dates, booleans, JSON. */
export function renderValue( key: string, value: unknown, linkTo?: string ): ReactNode {
	if ( value === null || value === undefined || value === '' ) {
		return <Typography.Text type="secondary">–</Typography.Text>;
	}
	const target = linkTo ?? LINKS[ key ];
	if ( target && ( typeof value === 'string' || typeof value === 'number' ) ) {
		return <Link to={ `/${ target }/show/${ encodeURIComponent( String( value ) ) }` }>{ String( value ) }</Link>;
	}
	if ( typeof value === 'boolean' ) {
		return value ? <Tag color="blue">yes</Tag> : <Tag>no</Tag>;
	}
	if ( typeof value === 'string' && isDateKey( key ) ) {
		const date = dayjs( value );
		return date.isValid() ? <Tooltip title={ value }>{ date.format( value.length <= 10 ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss' ) }</Tooltip> : value;
	}
	if ( TAGGED.has( key ) && typeof value === 'string' ) {
		return <Tag color={ COLORS[ value ] ?? 'default' }>{ value }</Tag>;
	}
	if ( Array.isArray( value ) && value.every( ( v ) => typeof v === 'string' ) ) {
		return value.length ? value.map( ( v ) => <Tag key={ v }>{ v }</Tag> ) : <Typography.Text type="secondary">–</Typography.Text>;
	}
	if ( typeof value === 'object' ) {
		return <Json value={ value } compact />;
	}
	if ( typeof value === 'string' && value.length > 120 ) {
		return <Tooltip title={ value }>{ `${ value.slice( 0, 117 ) }…` }</Tooltip>;
	}
	return String( value );
}

export function Json( { value, compact = false }: { value: unknown; compact?: boolean } ) {
	const text = JSON.stringify( value, null, compact ? 0 : 2 );
	if ( compact && text.length > 80 ) {
		return <Tooltip title={ <pre style={ { margin: 0, maxHeight: 400, overflow: 'auto' } }>{ JSON.stringify( value, null, 2 ) }</pre> }><code>{ `${ text.slice( 0, 77 ) }…` }</code></Tooltip>;
	}
	return compact ? <code>{ text }</code> : <pre style={ { margin: 0, maxHeight: 600, overflow: 'auto', fontSize: 12 } }>{ text }</pre>;
}
